import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Dialog from "../../../components/ui/Dialog";
import Pagination from "../../../components/ui/Pagination";
import BranchSelect from "../../../components/ui/BranchSelect";
import EmployeePicker from "../../../components/ui/EmployeePicker";
import {
  useFileOvertimeOnBehalf,
  useOvertimeList,
  useReviewOvertime,
} from "../../../api/queries/workflowQueries";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { getApiErrorMessage, getApiErrorStatus } from "../../../utils/apiError";
import {
  formatDateTime,
  formatPlainDate,
  formatTime,
  fromNow,
} from "../../../utils/dateTime";
import {
  OT_RULE,
  countedMinutes,
  formatMinutes,
  manilaIso,
  minutesBetween,
} from "./overtimeFormat";
import ApprovalTrail, { LevelBadge } from "./ApprovalTrail";
import { useCanModify } from "../../../hooks/usePermissions";

const EMPTY = [];
const STATUSES = [
  { value: "PENDING", label: "Pending", tone: "amber" },
  { value: "APPROVED", label: "Approved", tone: "green" },
  { value: "REJECTED", label: "Rejected", tone: "red" },
  { value: "CANCELLED", label: "Cancelled", tone: "gray" },
];
const TONE = Object.fromEntries(STATUSES.map((s) => [s.value, s.tone]));

/** A later level may lower the minutes, never raise them: the request's approvedMinutes so far (null before level 1). */
const minutesCap = (request) =>
  request.approvedMinutes ?? request.requestedMinutes ?? Infinity;

const ReviewDialog = ({ request, action, onClose }) => {
  const isReject = action === "REJECT";
  const cap = minutesCap(request);
  const [minutes, setMinutes] = useState(
    String(Number.isFinite(cap) ? cap : ""),
  );
  const [remarks, setRemarks] = useState("");
  const review = useReviewOvertime();
  const approved = Math.min(Number(minutes) || 0, cap);
  const invalid =
    !isReject && (!(Number(minutes) > 0) || Number(minutes) > cap);
  // One step: the first approve / reject is final
  const levels = request.levels;

  const submit = () =>
    review.mutate(
      {
        id: request.id,
        action,
        approvedMinutes:
          isReject || Number(minutes) === request.requestedMinutes
            ? undefined
            : Number(minutes),
        remarks: remarks.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success(isReject ? "Overtime rejected." : "Overtime approved.");
          onClose();
        },
        onError: (err) => {
          const code = getApiErrorStatus(err);
          // 403 (not one of the approvers) is already shown by the global handler
          if (code !== 403)
            toast[code === 409 ? "info" : "error"](
              getApiErrorMessage(err, "Could not review this request."),
            );
          onClose();
        },
      },
    );

  return (
    <Dialog
      title={isReject ? "Reject overtime?" : "Approve overtime?"}
      subtitle={`${request.employee?.name || ""} · ${formatPlainDate(request.date)} · ${formatTime(request.startTime)}–${formatTime(request.endTime)}`}
      onClose={onClose}
      busy={review.isPending}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={review.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`btn profile-btn ${isReject ? "btn-danger" : "btn-success"}`}
            onClick={submit}
            disabled={
              review.isPending || invalid || (isReject && !remarks.trim())
            }
          >
            {review.isPending && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            {isReject ? "Reject" : "Approve"}
          </button>
        </>
      }
    >
      <div className="eval-summary">
        <div className="fs-085">
          <strong>Requested:</strong> {formatMinutes(request.requestedMinutes)}
          {levels?.total > 1 && (
            <span className="ms-2">
              <LevelBadge levels={levels} status="PENDING" />
            </span>
          )}
        </div>
        {request.reason && (
          <div className="fs-080 text-slate-600 mt-1">{request.reason}</div>
        )}
      </div>
      {(request.approvals || EMPTY).length > 0 && (
        <div className="mb-3">
          <ApprovalTrail approvals={request.approvals} levels={levels} />
        </div>
      )}
      {!isReject && (
        <>
          <label htmlFor="ot-minutes" className="profile-label">
            Approved minutes
          </label>
          <input
            id="ot-minutes"
            type="number"
            min="1"
            max={Number.isFinite(cap) ? cap : undefined}
            className="form-control"
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
          />
          <div className="fs-075 mt-1">
            Counted as overtime:{" "}
            <strong>{formatMinutes(countedMinutes(approved))}</strong>
            <span className="text-slate-500"> · {OT_RULE}</span>
          </div>
          {cap < (request.requestedMinutes ?? Infinity) && (
            <div className="fs-070 text-slate-500">
              Already limited to {formatMinutes(cap)}; you can lower it, not
              raise it.
            </div>
          )}
        </>
      )}
      <label htmlFor="ot-remarks" className="profile-label mt-3">
        {isReject ? (
          <>
            Reason <span className="text-danger">*</span>
          </>
        ) : (
          "Remarks (optional)"
        )}
      </label>
      <textarea
        id="ot-remarks"
        className="form-control"
        rows={2}
        maxLength={500}
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        autoFocus={isReject}
      />
    </Dialog>
  );
};

/**
 * HR files overtime for an employee, e.g. when it couldn't be filed before the
 * work (employees can only file ahead). It goes through the normal approvals.
 */
const FileForEmployeeDialog = ({ onClose }) => {
  const user = useCurrentUser();
  const [employee, setEmployee] = useState(null);
  const [date, setDate] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState([]);
  const file = useFileOvertimeOnBehalf();
  const minutes = minutesBetween(start, end);
  const nextDay = !!start && !!end && end <= start;

  const submit = () => {
    const list = [];
    if (!employee) list.push("Choose the employee.");
    if (!date) list.push("Pick the date.");
    if (!start || !end) list.push("Enter the start and end times.");
    else if (countedMinutes(minutes) === 0)
      list.push("Under 30 minutes is not overtime.");
    if (!reason.trim()) list.push("Say why it is filed for the employee.");
    if (employee && employee.id === user.employeeId)
      list.push("You can't file overtime for yourself here.");
    if (list.length) return setErrors(list);
    setErrors([]);
    file.mutate(
      {
        employeeId: employee.id,
        date,
        startTime: manilaIso(date, start),
        endTime: manilaIso(date, end, nextDay),
        reason: reason.trim(),
      },
      {
        onSuccess: () => {
          toast.success("Overtime filed; it now goes through approval.");
          onClose();
        },
        onError: (err) => {
          const m = err?.response?.data?.message;
          setErrors(
            Array.isArray(m)
              ? m
              : [getApiErrorMessage(err, "Could not file it.")],
          );
        },
      },
    );
  };

  return (
    <Dialog
      title="File overtime for an employee"
      subtitle="For overtime that couldn't be filed before it was worked. It goes through the employee's normal approvals."
      onClose={onClose}
      busy={file.isPending}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={file.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={submit}
            disabled={file.isPending}
          >
            {file.isPending && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            File overtime
          </button>
        </>
      }
    >
      {errors.length > 0 && (
        <div className="alert alert-danger fs-085 py-2">
          <ul className="mb-0 ps-3">
            {errors.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="row g-3">
        <div className="col-12">
          <label htmlFor="ob-employee" className="profile-label">
            Employee <span className="text-danger">*</span>
          </label>
          <EmployeePicker
            id="ob-employee"
            companyId={user.companyId}
            value={employee}
            onChange={setEmployee}
            autoFocus
          />
        </div>
        <div className="col-md-4">
          <label htmlFor="ob-date" className="profile-label">
            Date <span className="text-danger">*</span>
          </label>
          <input
            id="ob-date"
            type="date"
            className="form-control"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <label htmlFor="ob-start" className="profile-label">
            Start <span className="text-danger">*</span>
          </label>
          <input
            id="ob-start"
            type="time"
            className="form-control"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <label htmlFor="ob-end" className="profile-label">
            End <span className="text-danger">*</span>
          </label>
          <input
            id="ob-end"
            type="time"
            className="form-control"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </div>
        {start && end && (
          <div className="col-12 fs-075 text-slate-500 mt-1">
            {formatMinutes(minutes)}
            {nextDay && " (ends the next day)"} · counts as{" "}
            {formatMinutes(countedMinutes(minutes))} if fully approved and
            worked
          </div>
        )}
        <div className="col-12">
          <label htmlFor="ob-reason" className="profile-label">
            Reason <span className="text-danger">*</span>
          </label>
          <textarea
            id="ob-reason"
            className="form-control"
            rows={2}
            maxLength={1000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Asked to stay for the inventory; couldn't file in time"
          />
        </div>
      </div>
    </Dialog>
  );
};

/**
 * Overtime requests: pending by default; approve (possibly fewer minutes) or reject.
 * shared = { companyId, branchId, assignmentType, search }: inside the Time
 * module, whose filter bar replaces this list's own search and branch.
 */
const OvertimeQueue = ({ shared }) => {
  const [params, setParams] = useSearchParams();
  const status = STATUSES.some((s) => s.value === params.get("status"))
    ? params.get("status")
    : "PENDING";
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [ownBranchId, setBranchId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [searchText, setSearchText] = useState("");
  const [ownSearch, setSearch] = useState("");
  const branchId = shared ? shared.branchId || "" : ownBranchId;
  const search = shared ? shared.search || "" : ownSearch;
  const [reviewing, setReviewing] = useState(null);
  const [filing, setFiling] = useState(false);
  const canModify = useCanModify("overtime"); // + the server's canReview (my turn)
  // Filing for someone: overtime modify at COMPANY scope (HR)
  const canFileForOthers = useCanModify("overtime", { company: true });

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchText.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchText]);

  const query = useOvertimeList({
    status,
    branchId,
    from,
    to,
    search,
    companyId: shared?.companyId || undefined,
    assignmentType: shared?.assignmentType || undefined,
    page,
    limit,
  });
  useEffect(() => setPage(1), [branchId, search, shared?.assignmentType]);
  const rows = query.data?.data || EMPTY;
  const setStatus = (value) => {
    const next = new URLSearchParams(params);
    value === "PENDING" ? next.delete("status") : next.set("status", value);
    setParams(next, { replace: true });
    setPage(1);
  };

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div
          className="approval-subtabs"
          role="tablist"
          aria-label="Overtime status"
        >
          {STATUSES.map((s) => (
            <button
              key={s.value}
              type="button"
              role="tab"
              aria-selected={status === s.value}
              className={`approval-subtab ${status === s.value ? "active" : ""}`}
              onClick={() => setStatus(s.value)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <span className="fs-075 text-slate-500">
            <i className="bi bi-info-circle me-1"></i>
            {OT_RULE}
          </span>
          {canFileForOthers && (
            <button
              type="button"
              className="btn btn-sm btn-light border"
              onClick={() => setFiling(true)}
            >
              <i className="bi bi-plus-lg me-1"></i>File for an employee
            </button>
          )}
        </div>
      </div>

      <div className="approval-filters mb-3">
        {!shared && (
          <div className="position-relative approval-search">
            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
            <input
              type="search"
              className="filter-input w-100 ps-5"
              placeholder="Search name or employee number"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              aria-label="Search employees"
            />
          </div>
        )}
        <div className="d-flex align-items-center gap-1">
          <input
            type="date"
            className="filter-input"
            value={from}
            max={to || undefined}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
            aria-label="From"
          />
          <span className="text-slate-400">–</span>
          <input
            type="date"
            className="filter-input"
            value={to}
            min={from || undefined}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
            aria-label="To"
          />
        </div>
        {!shared && (
          <div className="att-filter-branch">
            <BranchSelect
              value={branchId}
              onChange={(v) => {
                setBranchId(v || "");
                setPage(1);
              }}
              id="ot-branch"
            />
          </div>
        )}
        {query.isFetching && !query.isLoading && (
          <span
            className="spinner-border spinner-border-sm text-primary"
            role="status"
            aria-label="Refreshing"
          ></span>
        )}
      </div>

      <div className="glass-table-card">
        {query.isError ? (
          <div className="approval-empty text-danger">
            <i className="bi bi-exclamation-triangle"></i>
            {getApiErrorMessage(
              query.error,
              "Could not load overtime requests.",
            )}
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-hover mb-0 approval-table">
                <thead className="custom-table-header">
                  <tr>
                    <th className="ps-4 py-3">Employee</th>
                    <th className="py-3">Date · Time</th>
                    <th className="py-3 text-end">Requested</th>
                    <th className="py-3 text-end">Counted</th>
                    <th className="py-3">Reason</th>
                    <th className="py-3">Status</th>
                    <th className="py-3 pe-4 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="ps-4">
                        <div className="emp-name-text">
                          {r.employee?.name || "—"}
                        </div>
                        <div className="emp-sub-text">
                          {r.employee?.employeeNumber}
                          {r.employee?.branchName
                            ? ` · ${r.employee.branchName}`
                            : ""}
                        </div>
                      </td>
                      <td className="text-nowrap">
                        {formatPlainDate(r.date, "ddd, MMM D")}
                        <div className="fs-070 text-slate-500">
                          {formatTime(r.startTime)}–{formatTime(r.endTime)}
                        </div>
                      </td>
                      <td className="text-end tabular-nums">
                        {formatMinutes(r.requestedMinutes)}
                      </td>
                      <td className="text-end tabular-nums">
                        {r.status === "APPROVED" ? (
                          formatMinutes(r.countedMinutes)
                        ) : (
                          <span
                            className="text-slate-400"
                            title="If fully approved"
                          >
                            {formatMinutes(countedMinutes(r.requestedMinutes))}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="approval-reason" title={r.reason}>
                          {r.reason || "—"}
                        </div>
                        {r.filedLate && (
                          <span
                            className="chip chip--sm chip--alt"
                            title="Filed by HR after the overtime started"
                          >
                            Filed late by HR
                          </span>
                        )}
                      </td>
                      <td>
                        <span
                          className={`status-pill status-pill--${TONE[r.status] || "gray"}`}
                        >
                          {r.status}
                        </span>
                        {r.status === "PENDING" && (
                          <span className="ms-1">
                            <LevelBadge levels={r.levels} status={r.status} />
                          </span>
                        )}
                        {r.reviewRemarks && (
                          <div className="fs-070 text-slate-500 fst-italic">
                            “{r.reviewRemarks}”
                          </div>
                        )}
                        <div
                          className="fs-070 text-slate-400"
                          title={formatDateTime(r.reviewedAt || r.createdAt)}
                        >
                          {fromNow(r.reviewedAt || r.createdAt)}
                        </div>
                      </td>
                      <td className="pe-4 text-end text-nowrap">
                        {r.status === "PENDING" &&
                          r.canReview === false &&
                          r.levels?.total > 1 && (
                            <span className="fs-070 text-slate-400">
                              Another approver is deciding
                            </span>
                          )}
                        {r.status === "PENDING" &&
                          r.canReview !== false &&
                          canModify && (
                            <div className="d-inline-flex gap-1">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger att-action-btn"
                                onClick={() =>
                                  setReviewing({ request: r, action: "REJECT" })
                                }
                              >
                                Reject
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-success att-action-btn"
                                onClick={() =>
                                  setReviewing({
                                    request: r,
                                    action: "APPROVE",
                                  })
                                }
                              >
                                Approve
                              </button>
                            </div>
                          )}
                      </td>
                    </tr>
                  ))}
                  {!query.isLoading && rows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="approval-empty">
                        <i className="bi bi-clock"></i>
                        {status === "PENDING"
                          ? "No overtime waiting for you."
                          : "No overtime requests."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {query.isLoading && (
              <div className="text-center py-5">
                <span
                  className="spinner-border text-primary"
                  role="status"
                  aria-label="Loading"
                ></span>
              </div>
            )}
            <Pagination
              page={page}
              limit={limit}
              total={query.data?.total || 0}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setLimit(l);
                setPage(1);
              }}
            />
          </>
        )}
      </div>

      {filing && <FileForEmployeeDialog onClose={() => setFiling(false)} />}
      {reviewing && (
        <ReviewDialog
          request={reviewing.request}
          action={reviewing.action}
          onClose={() => setReviewing(null)}
        />
      )}
    </>
  );
};

export default OvertimeQueue;
